import React from 'react'
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  Tooltip
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEdit,
  faTrash,
  faPaperPlane,
  faUsers
} from '@fortawesome/free-solid-svg-icons'
import PropTypes from 'prop-types'
import { StatusChip } from '../atoms'
import { colors } from '../../consts'

const SmsAutomationCard = ({
  automation,
  onEdit,
  onDelete,
  onToggle,
  onTest,
  onManageCampaigns,
  className
}) => {
  const configDispositions = automation.config?.trigger?.dispositions
  const legacyDisposition = automation.config?.trigger?.disposition
  const dispositionsList = Array.isArray(configDispositions) && configDispositions.length > 0
    ? configDispositions
    : legacyDisposition ? [legacyDisposition] : []
  const template = automation.config?.sms?.template || ''
  const truncatedTemplate = template.length > 80
    ? template.substring(0, 80) + '...'
    : template
  const campaignCount = Object.keys(automation.campaign_subscriptions || {}).length

  return (
    <Card
      className={className}
      style={{
        borderColor: automation.enabled ? colors.primary : undefined,
        opacity: automation.enabled ? 1 : 0.7
      }}
    >
      <CardContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <Typography variant='h6' style={{ flex: 1 }}>
            {automation.name}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={automation.enabled}
                onChange={(e) => onToggle(automation.id, e.target.checked)}
                color='primary'
              />
            }
            label='Enabled'
            labelPlacement='start'
          />
        </div>

        <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {dispositionsList.length > 0 ? dispositionsList.map(d => (
            <StatusChip
              key={d}
              label={d}
              color='info'
              variant='outlined'
              size='small'
            />
          )) : (
            <StatusChip label='Not configured' color='default' variant='outlined' size='small' />
          )}
        </div>

        {campaignCount > 0 && (
          <Typography variant='caption' color='textSecondary' style={{ marginBottom: 4, display: 'block' }}>
            {campaignCount} campaign{campaignCount !== 1 ? 's' : ''}
          </Typography>
        )}

        {truncatedTemplate && (
          <Typography variant='body2' color='textSecondary' style={{ marginBottom: 8, fontStyle: 'italic' }}>
            &ldquo;{truncatedTemplate}&rdquo;
          </Typography>
        )}
      </CardContent>

      <CardActions>
        <Tooltip title='Edit Automation'>
          <span>
            <Button
              size='small'
              startIcon={<FontAwesomeIcon icon={faEdit} />}
              onClick={() => onEdit(automation)}
              style={{ textTransform: 'none' }}
            >
              Edit
            </Button>
          </span>
        </Tooltip>

        <Tooltip title='Manage Campaign Subscriptions'>
          <Button
            size='small'
            startIcon={<FontAwesomeIcon icon={faUsers} />}
            onClick={() => onManageCampaigns(automation)}
            style={{ textTransform: 'none', fontWeight: 600 }}
          >
            Campaigns
          </Button>
        </Tooltip>

        <Tooltip title='Send Test SMS'>
          <span>
            <Button
              size='small'
              startIcon={<FontAwesomeIcon icon={faPaperPlane} />}
              onClick={() => onTest(automation)}
              disabled={!automation.enabled}
              style={{ textTransform: 'none' }}
            >
              Test
            </Button>
          </span>
        </Tooltip>

        <Tooltip title='Delete Automation'>
          <Button
            size='small'
            color='secondary'
            startIcon={<FontAwesomeIcon icon={faTrash} />}
            onClick={() => onDelete(automation.id)}
            style={{ marginLeft: 'auto', textTransform: 'none', fontWeight: 600 }}
          >
            Delete
          </Button>
        </Tooltip>
      </CardActions>
    </Card>
  )
}

SmsAutomationCard.propTypes = {
  automation: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onTest: PropTypes.func.isRequired,
  onManageCampaigns: PropTypes.func.isRequired,
  className: PropTypes.string
}

export default SmsAutomationCard
